import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { MailCheck } from 'lucide-react';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { COUNTRY_LOCALE } from '@nokowazzi/types';
import { Button, EmptyState, Input, RadioGroup, RadioItem, toast } from '@nokowazzi/ui';
import { AuthScreen } from '../features/auth/AuthScreen';
import {
  COUNTRY_OPTIONS,
  signUpSchema,
  translateFirstError,
  type CountryOption,
} from '../features/auth/forms';
import { supabase } from '../lib/supabase';

export const Route = createFileRoute('/auth/signup')({
  component: SignUpScreen,
});

interface FieldErrors {
  email?: string;
  password?: string;
  displayName?: string;
}

function SignUpScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [country, setCountry] = useState<CountryOption>('KR');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  /** 이메일 확인이 켜져 있어 세션 없이 가입된 경우 — 확인 안내 화면 */
  const [confirmationEmail, setConfirmationEmail] = useState<string | null>(null);

  const applyLocale = (locale: string) => {
    if (i18n.language !== locale) {
      void i18n.changeLanguage(locale);
      document.documentElement.lang = locale;
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;

    const parsed = signUpSchema.safeParse({ email, password, displayName, country });
    if (!parsed.success) {
      const fieldErrors = parsed.error.flatten().fieldErrors;
      setErrors({
        email: translateFirstError(t, fieldErrors.email),
        password: translateFirstError(t, fieldErrors.password),
        displayName: translateFirstError(t, fieldErrors.displayName),
      });
      return;
    }

    setErrors({});
    setSubmitting(true);
    const locale = COUNTRY_LOCALE[parsed.data.country];
    const { data, error } = await supabase.auth.signUp({
      email: parsed.data.email,
      password: parsed.data.password,
      options: {
        data: {
          display_name: parsed.data.displayName,
          country: parsed.data.country,
          locale,
        },
      },
    });
    setSubmitting(false);

    if (error) {
      toast.show({ type: 'error', message: t('auth.signUpFailed') });
      return;
    }

    // 이메일 확인 활성 시 기존 이메일로 가입하면 error 없이 identities가 빈 배열로 온다
    if (data.user && data.user.identities?.length === 0) {
      setErrors({ email: t('auth.errors.emailInUse') });
      toast.show({ type: 'error', message: t('auth.errors.emailInUse') });
      return;
    }

    applyLocale(locale);

    if (data.session) {
      void navigate({ to: '/', replace: true });
    } else {
      setConfirmationEmail(parsed.data.email);
    }
  };

  if (confirmationEmail !== null) {
    return (
      <AuthScreen title={t('auth.signUpTitle')}>
        <EmptyState
          icon={<MailCheck size={48} strokeWidth={1.5} />}
          title={t('auth.confirmEmailTitle')}
          description={t('auth.confirmEmailDescription', { email: confirmationEmail })}
          action={
            <Button
              variant="tertiary"
              onClick={() => void navigate({ to: '/auth/signin', replace: true })}
            >
              {t('actions.signIn')}
            </Button>
          }
        />
      </AuthScreen>
    );
  }

  return (
    <AuthScreen title={t('auth.signUpTitle')}>
      <form noValidate className="flex flex-col gap-4" onSubmit={(e) => void handleSubmit(e)}>
        <Input
          type="email"
          label={t('auth.email')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
          autoComplete="email"
          inputMode="email"
          autoCapitalize="none"
        />
        <Input
          type="password"
          label={t('auth.password')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errors.password}
          helperText={t('auth.passwordHelper')}
          autoComplete="new-password"
        />
        <Input
          label={t('auth.displayName')}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          error={errors.displayName}
          autoComplete="nickname"
          maxLength={40}
          showCount
        />
        <fieldset className="flex flex-col gap-1.5">
          <legend className="text-caption1 text-fg-secondary">{t('auth.country')}</legend>
          <RadioGroup
            className="mt-1.5 flex-row gap-8"
            value={country}
            onValueChange={(value) => {
              if (value === 'KR' || value === 'JP') setCountry(value);
            }}
            aria-label={t('auth.country')}
          >
            {COUNTRY_OPTIONS.map((option) => (
              <RadioItem
                key={option}
                value={option}
                label={option === 'KR' ? t('auth.countryKr') : t('auth.countryJp')}
              />
            ))}
          </RadioGroup>
        </fieldset>
        <Button type="submit" size="lg" fullWidth loading={submitting} className="mt-2">
          {t('actions.signUp')}
        </Button>
      </form>
      <p className="mt-6 text-center text-caption1 text-fg-secondary">
        {t('auth.haveAccount')}{' '}
        <Link
          to="/auth/signin"
          className="font-semibold text-fg underline underline-offset-2"
        >
          {t('actions.signIn')}
        </Link>
      </p>
    </AuthScreen>
  );
}
